import { LoanData, LoanStatus, USER_TYPES } from './LoanData';

export const PARTICIPANT_MODES = {
  BROKER_ONLY: 'broker_only',
} as const;

export type ParticipantMode = typeof PARTICIPANT_MODES[keyof typeof PARTICIPANT_MODES];

// Backward-compatible alias
export const BROKER_ONLY_PARTICIPANT_MODE = PARTICIPANT_MODES.BROKER_ONLY;

export function getParticipantMode(loanData: LoanData | null | undefined): ParticipantMode | undefined {
  // Only the opt-in mode is stored so existing loan blobs continue through Broker + Borrower.
  const mode = loanData?.participantMode?.toLowerCase();
  return mode === PARTICIPANT_MODES.BROKER_ONLY ? PARTICIPANT_MODES.BROKER_ONLY : undefined;
}

export function isBrokerOnlyLoan(loanData: LoanData | null | undefined): boolean {
  return getParticipantMode(loanData) === PARTICIPANT_MODES.BROKER_ONLY;
}

export function isBrokerBasedLoan(loanData: LoanData | null | undefined): boolean {
  if (!loanData) {
    return false;
  }

  if (loanData.userType === USER_TYPES.BROKER) {
    return true;
  }

  return loanData.userType === USER_TYPES.LOAN_OFFICER && Boolean(loanData.brokerInfo?.email);
}

export function canEditParticipantMode(
  loanData: LoanData | null | undefined,
  isExistingLoan: boolean
): boolean {
  // A Draft can be reopened, so status alone cannot distinguish it from initial setup.
  return isBrokerBasedLoan(loanData) && !isExistingLoan;
}

/** Signer scope used when an offline document belongs to the main signer rather than a guarantor. */
export const MAIN_SIGNER_ID = 'main';

/** Signer scope that lists every signer's offline documents instead of one signer's. */
export const ALL_SIGNERS_ID = 'all';

/**
 * Single stage gate for every Broker Only offline task. The main signer signs during
 * pre-qualification, additional guarantors sign afterwards to match their existing sign-request
 * availability, and payment authorisation runs in processing.
 */
export function isOfflineStageAllowed(
  task: 'disclosures' | 'payment_authorization',
  loanStatus?: string,
  guarantorId?: string | null,
  fastTrack = false
): boolean {
  if (task === 'payment_authorization') {
    return loanStatus === LoanStatus.PROCESSING;
  }
  const signerId = guarantorId?.trim();
  // Fast-track loans skip Pre-Qualification, so the main signer completes disclosures in Processing.
  return signerId && signerId !== MAIN_SIGNER_ID
    ? loanStatus !== LoanStatus.DRAFT && loanStatus !== LoanStatus.PRE_QUALIFIED
    : loanStatus === LoanStatus.PRE_QUALIFIED || (fastTrack && loanStatus === LoanStatus.PROCESSING);
}
